import keycloak from "./Keycloak";

/*
 * Keeping the Keycloak session usable: refreshing the access token before it is needed, and sending
 * the user back through login once the session is beyond saving.
 *
 * CANONICAL IMPLEMENTATION - source of truth is magic-penguin,
 * src/app/components/auth/auth-session.service.ts. Keep the two in step: a fix here belongs there
 * and in every other tool that carries a copy. There is no shared package because the platform has
 * no private npm registry.
 *
 * Both thresholds below are compared against the `exp` of the current token, never against the
 * realm's configured access token lifespan. That is what lets the same values work with a 5 minute
 * token and with an 8 hour one. Do not replace them with a value derived from a lifespan - that is
 * the mistake that left training-tool with a hardcoded updateToken(1770).
 */

/** Used per request by the axios interceptor. */
export const TOKEN_MIN_VALIDITY_SECONDS = 30;

/**
 * Used by the keepalive heartbeat. Must stay above the heartbeat's tick interval, otherwise a tick
 * can step over the window in which the token is close to expiring and let it lapse.
 */
export const KEEPALIVE_MIN_VALIDITY_SECONDS = 90;

/** How long to wait for Keycloak's token endpoint before giving up on a refresh. */
const REFRESH_TIMEOUT_MS = 5_000;

/**
 * How long to stop attempting refreshes after one timed out.
 *
 * keycloak-js drains its internal refresh queue only from the call that started the request, so a
 * request that never settles leaves the queue occupied and every later updateToken() waits forever
 * as well. Backing off turns that into one stalled wait per cooldown instead of one per request.
 */
const UNAVAILABLE_COOLDOWN_MS = 30_000;

class RefreshTimeoutError extends Error {}

let unavailableUntil = 0;
let redirectingToLogin = false;

/** True while refreshes are being skipped because Keycloak stopped answering in time. */
export function refreshUnavailable() {
    return Date.now() < unavailableUntil;
}

function withTimeout(promise) {
    let timer;

    const expiry = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new RefreshTimeoutError()), REFRESH_TIMEOUT_MS);
    });

    // keycloak-js exposes no way to abort its request, so after a timeout the original promise is
    // simply abandoned. Swallow its eventual outcome to keep it from surfacing as an unhandled
    // rejection once nobody is waiting on it any more.
    promise.catch(() => undefined);

    return Promise.race([promise, expiry]).finally(() => clearTimeout(timer));
}

/**
 * Makes sure the access token has at least `minValidity` seconds of life left. Pass -1 to refresh
 * regardless of how much is left.
 *
 * Resolves instead of rejecting when Keycloak does not answer in time, leaving the caller to send
 * whatever token it has and let the backend judge it. Waiting indefinitely would freeze every
 * request in the application for as long as Keycloak stayed unreachable, which is a far worse
 * failure than one rejected request.
 *
 * Still rejects when the refresh genuinely fails, because that means the session is gone.
 */
export async function ensureFresh(minValidity) {
    if (!keycloak.authenticated || refreshUnavailable()) {
        return;
    }

    try {
        await withTimeout(keycloak.updateToken(minValidity));
    } catch (error) {
        if (error instanceof RefreshTimeoutError) {
            unavailableUntil = Date.now() + UNAVAILABLE_COOLDOWN_MS;
            return;
        }
        throw error;
    }
}

/**
 * Re-authenticate, returning to the page the user was on rather than to the homepage.
 *
 * Guarded because a dead session can surface on several requests at once, and each would otherwise
 * start its own navigation and store its own PKCE state.
 */
export function redirectToLogin() {
    if (redirectingToLogin) {
        return;
    }
    redirectingToLogin = true;

    // On success this promise never settles, because the browser navigates away. It only rejects
    // when the navigation could not be started, in which case the guard has to be released or every
    // later attempt would be swallowed and the app would sit there with no explanation.
    keycloak.login({redirectUri: window.location.href}).catch((error) => {
        redirectingToLogin = false;
        console.warn("[auth] could not start login", error);
    });
}
